import { helperCreateObjectUrl, onNextFrame } from "~/solidJs/shared/helpers";
import { getImageObjectFromCanvas, getResizedCanvas } from "~/solidJs/shared/helpers/canvasHelpers";
import { initObject3DViewerHoverMenu } from "~/solidJs/widgets/HoverFeatureMenu";
import Viewer from "../viewer";

export default class Object3DViewer extends Viewer {
	/**
	 * @type
	 */
	object3d;
	static type = "object3d";
	static supportedExtensions = ["glb", "gltf", "obj", "fbx", "stl"];

	constructor(asset, socketsOutput, socketName, serializedCameraPosition, onCameraSerialized) {
		super(asset, socketsOutput, socketName);
		this.setupPromise = this.setupThreeDependecis().then(() => {
			this.setupThree();
			this.addMaterialModes();
			this.onCameraSerialized = onCameraSerialized;
			if (serializedCameraPosition) {
				this.deSerializeCameraState(serializedCameraPosition);
			}
			// Removed old addCanvasToImageButton - now handled by SolidJS
		});

		// Initialize new hover feature menu
		if (socketsOutput && socketName) {
			this.hoverMenuCleanup = initObject3DViewerHoverMenu(this, socketsOutput, socketName);
		}
	}

	getViewerType() {
		return "object-3d";
	}
	get matcap() {
		return {
			light: this.textureLoader.load("/public/matcap-light.jpeg")
		};
	}

	async setupThreeDependecis() {
		const [THREE, OrbitControls] = await Promise.all([
			import("three"),
			import("three/examples/jsm/controls/OrbitControls")
		]);
		this.THREE = THREE;
		this.OrbitControls = OrbitControls.OrbitControls;
		this.textureLoader = new this.THREE.TextureLoader();
		this.materials = {
			wireframe: new this.THREE.MeshBasicMaterial({
				wireframe: true,
				color: new this.THREE.Color("white")
			}),
			normal: new this.THREE.MeshNormalMaterial(),
			matcapLight: new this.THREE.MeshMatcapMaterial({ matcap: this.matcap.light }),
			matcapLightVertexColor: "initMaterial"
		};
	}

	setupThree() {
		this.renderer = new this.THREE.WebGLRenderer({ antialias: true, alpha: true });
		const setSize = () => {
			this.renderer.setSize(this.dom.viewer.offsetWidth, this.dom.viewer.offsetWidth);
			this.update();
		};
		const viewerResizeObserver = new ResizeObserver(() => {
			setSize();
		});
		viewerResizeObserver.observe(this.dom.viewer);

		this.scene = new this.THREE.Scene();
		this.scene.background = new this.THREE.Color(0xf6f6f6);

		this.camera = new this.THREE.PerspectiveCamera(60, 1, 0.1, 200);
		this.camera.position.set(0, 5, 5);
		this.scene.add(this.camera);

		this.grid = new this.THREE.GridHelper(10, 10, 0xaaaaaa, 0xaaaaaa);
		this.scene.add(this.grid);

		this.controls = new this.OrbitControls(this.camera, this.renderer.domElement);
		this.controls.addEventListener("change", () => {
			this.serializeCameraStateTimeout();
			this.update();
		});

		this.renderer.domElement.addEventListener("mousedown", (e) => e.stopPropagation());
		this.renderer.domElement.addEventListener("mouseover", (e) => e.stopPropagation());
		this.renderer.domElement.addEventListener("wheel", (e) => e.stopPropagation());
		this.domElement().appendChild(this.renderer.domElement);
		this.update();
		setSize();
	}

	serializeCameraStateTimeout() {
		clearTimeout(this.serializingTimeout);
		this.serializingTimeout = setTimeout(() => {
			this.serializeCameraState();
		}, 250);
	}

	serializeCameraState() {
		const cameraState = this.camera.matrix.toArray();
		this.onCameraSerialized?.(cameraState);
	}

	deSerializeCameraState(cameraState) {
		this.camera.matrix.fromArray(cameraState);
		this.camera.matrix.decompose(
			this.camera.position,
			this.camera.quaternion,
			this.camera.scale
		);
	}

	update() {
		this.renderer.render(this.scene, this.camera);
	}
	removeItemsForRender() {
		this.scene.background = null;
		this.scene.remove(this.grid);
	}
	addItemsAfterRender() {
		this.scene.background = new this.THREE.Color(0xf6f6f6);
		this.scene.add(this.grid);
	}
	async serializeAsFile() {
		const originalPixelRatio = this.renderer.getPixelRatio();
		const originalSize = {
			width: this.renderer.domElement.width,
			height: this.renderer.domElement.height
		};

		// Increase pixel ratio for better quality
		this.renderer.setPixelRatio(2); // Use higher pixel ratio for rendering

		// Set larger size for higher resolution output
		const renderWidth = 2048; // Increased from 2048
		const renderHeight = 2048; // Increased from 2048
		this.renderer.setSize(renderWidth, renderHeight);
		this.removeItemsForRender();
		this.update();
		const resizedRenderCanvas = getResizedCanvas(
			this.renderer.domElement,
			renderWidth,
			renderHeight
		);
		if (!resizedRenderCanvas) return null;
		const imageFile = getImageObjectFromCanvas(resizedRenderCanvas, "render.png", "image/png");
		this.renderer.setPixelRatio(originalPixelRatio);
		this.renderer.setSize(originalSize.width, originalSize.height);
		this.addItemsAfterRender();
		this.update();
		return imageFile;
	}
	async loadAsync() {
		const extension = this.asset.name.split(".").pop().toLowerCase();
		const url = helperCreateObjectUrl(this.asset);
		this.object3d = await ModelLoader.loadModelAsync(url, extension);
		const promises = [];
		const extractInitMaterial = async (child) => {
			if (child instanceof this.THREE.Mesh) {
				const initMaterial = child.material;
				if (initMaterial.map?.image) {
					// Load the texture image
					const textureLoader = this.textureLoader;
					const src = await this.getImageBitmapSrc(initMaterial.map.image);
					const texture = textureLoader.load(src);
					texture.flipY = false;
					this.initMaterial = new this.THREE.MeshBasicMaterial({
						map: texture,
						color: new this.THREE.Color("white")
					});
				}
			}
		};
		this.object3d.traverse((child) => {
			promises.push(extractInitMaterial(child));
		});
		await Promise.allSettled(promises);
		// Calculate bounding box
		const boundingBox = new this.THREE.Box3().setFromObject(this.object3d);
		const center = boundingBox.getCenter(new this.THREE.Vector3());
		const size = boundingBox.getSize(new this.THREE.Vector3());

		// Get the maximum dimension of the object
		const maxDim = Math.max(size.x, size.y, size.z);

		// Calculate scale to normalize size
		const scale = 8 / maxDim; // You can adjust the number 5 to change the relative size

		// Apply scale and center the object
		this.object3d.scale.setScalar(scale);
		this.object3d.position.sub(center.multiplyScalar(scale));

		this.scene.add(this.object3d);

		// Position camera to fit the object
		const distance = 5; // Adjust this value to change how far the camera is
		this.camera.position.set(distance, distance, distance);
		this.camera.lookAt(0, 0, 0);

		// Update controls target to center of object
		this.controls.target.set(0, 0, 0);
		this.controls.update();

		this.setMaterialMode("vertex-color");
		this.update();
		onNextFrame(() => {
			setTimeout(() => this.update(), 0);
		});
	}

	async reloadAssetAsync(asset) {
		if (this.object3d) {
			this.object3d.traverse((child) => {
				if (child instanceof this.THREE.Mesh) child.geometry.dispose();
			});
			this.scene.remove(this.object3d);
			this.object3d = undefined;
		}

		this.asset = asset;
		await this.loadAsync();
	}

	async getImageBitmapSrc(bmp) {
		const canvas = this.canvas || document.createElement("canvas");
		this.canvas = canvas;
		canvas.width = bmp.width;
		canvas.height = bmp.height;
		const ctx = canvas.getContext("bitmaprenderer");
		ctx.transferFromImageBitmap(bmp);

		const blob = await new Promise((res) => canvas.toBlob(res));
		return URL.createObjectURL(blob);
	}

	setMaterial(material) {
		if (!material || !this.object3d) return;

		this.object3d.traverse(async (child) => {
			if (child instanceof this.THREE.Mesh) {
				child.material = material;
			}
		});
		this.update();
	}

	addMaterialModes() {
		const selectElement = document.createElement("select");
		selectElement.style.position = "absolute";
		selectElement.style.width = "fit-content";
		selectElement.style.top = "0";
		selectElement.style.left = "0";

		const materialOptions = {};
		const addMaterialOption = (optionValue, material, isSelected = false) => {
			materialOptions[optionValue] = material;
			const option = document.createElement("option");
			option.value = optionValue;
			option.textContent = optionValue;
			option.selected = isSelected;
			selectElement.appendChild(option);
		};
		addMaterialOption("normal-matcap", this.materials.normal, true);
		addMaterialOption("light-matcap", this.materials.matcapLight);
		addMaterialOption("vertex-color", this.materials.matcapLightVertexColor);
		addMaterialOption("wireframe", this.materials.wireframe);

		/**
		 * @param {string} optionValue normal-matcap | light-matcap | vertex-color | wireframe
		 */
		this.setMaterialMode = (optionValue) => {
			selectElement.value = optionValue;
			let material = materialOptions[optionValue];
			if (material === "initMaterial") {
				material = this.initMaterial || this.materials.matcapLight;
			}
			this.setMaterial(material);
		};

		selectElement.addEventListener("mousedown", (e) => e.stopPropagation());
		selectElement.addEventListener("change", (e) => {
			this.setMaterialMode(e.target.value);
		});

		this.domElement().append(selectElement);
		onNextFrame(() => {
			setTimeout(() => this.setMaterialMode("vertex-color"), 0);
		});
	}
	// Hover functionality now handled by SolidJS HoverFeatureMenu
}
const importThreeLoaders = async () => {
	const loaders = [
		import("three/examples/jsm/loaders/GLTFLoader"),
		import("three/examples/jsm/loaders/FBXLoader"),
		import("three/examples/jsm/loaders/OBJLoader"),
		import("three/examples/jsm/loaders/STLLoader"),
		import("three")
	];
	const awaitedLoaders = await Promise.all(loaders);

	return {
		glb: {
			loader: awaitedLoaders[0].GLTFLoader,
			handleWay: (model) => model.scene
		},
		gltf: {
			loader: awaitedLoaders[0].GLTFLoader,
			handleWay: (model) => model.scene
		},
		fbx: {
			loader: awaitedLoaders[1].FBXLoader,
			handleWay: (model) => model
		},
		obj: {
			loader: awaitedLoaders[2].OBJLoader,
			handleWay: (model) => model
		},
		stl: {
			loader: awaitedLoaders[3].STLLoader,
			handleWay: (model) =>
				new awaitedLoaders[4].default.Mesh(model, new awaitedLoaders[4].MeshBasicMaterial())
		}
	};
};

class ModelLoader {
	static loaders = importThreeLoaders();

	static async getLoaderAndHandleWayByExtension(extension) {
		const loaders = await ModelLoader.loaders;

		return loaders[extension];
	}

	static async loadModelAsync(url, extension) {
		const { loader, handleWay } = await ModelLoader.getLoaderAndHandleWayByExtension(extension);

		if (!loader || !handleWay) {
			alert(`Unsupported 3D model format: ${extension}`);
			return;
		}
		const initedLoader = new loader();
		const loadResult = await initedLoader.loadAsync(url);
		return handleWay(loadResult);
	}
}
